import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import LandingInspector from "./LandingInspector";
import { BRAND, emptyHighlight, emptySlide } from "./config";
import { SITE_SECTION_META } from "./siteBuilderModel";

const clone = (value) => {
  if (typeof structuredClone === "function") return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
};

export default function LandingSectionDialog({
  open,
  activeSection,
  form,
  productsEnabled,
  saving,
  onClose,
  onSave,
  onOpenSettings,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [draft, setDraft] = React.useState(form);
  const [error, setError] = React.useState("");
  const wasOpenRef = React.useRef(false);

  React.useEffect(() => {
    if (open && !wasOpenRef.current) {
      setDraft(clone(form));
      setError("");
    }
    wasOpenRef.current = open;
  }, [open, form]);

  const setRoot = React.useCallback((field, value) => {
    setDraft((previous) => ({ ...previous, [field]: value }));
  }, []);

  const setGroup = React.useCallback((group, field, value) => {
    setDraft((previous) => ({
      ...previous,
      [group]: { ...(previous[group] || {}), [field]: value },
    }));
  }, []);

  const setNested = React.useCallback((group, nested, field, value) => {
    setDraft((previous) => ({
      ...previous,
      [group]: {
        ...(previous[group] || {}),
        [nested]: {
          ...(previous[group]?.[nested] || {}),
          [field]: value,
        },
      },
    }));
  }, []);

  const addSlide = React.useCallback(() => {
    setDraft((previous) => ({
      ...previous,
      hero: {
        ...previous.hero,
        slides: [...(Array.isArray(previous.hero?.slides) ? previous.hero.slides : []), { ...emptySlide }].slice(0, 6),
      },
    }));
  }, []);

  const updateSlide = React.useCallback((index, field, value) => {
    setDraft((previous) => ({
      ...previous,
      hero: {
        ...previous.hero,
        slides: (Array.isArray(previous.hero?.slides) ? previous.hero.slides : []).map((item, itemIndex) =>
          itemIndex === index ? { ...item, [field]: value } : item
        ),
      },
    }));
  }, []);

  const removeSlide = React.useCallback((index) => {
    setDraft((previous) => ({
      ...previous,
      hero: {
        ...previous.hero,
        slides: (Array.isArray(previous.hero?.slides) ? previous.hero.slides : []).filter((_, itemIndex) => itemIndex !== index),
      },
    }));
  }, []);

  const addHighlight = React.useCallback(() => {
    setDraft((previous) => ({
      ...previous,
      highlights: [
        ...(Array.isArray(previous.highlights) ? previous.highlights : []),
        { ...emptyHighlight, id: `diferencial-${Date.now()}` },
      ].slice(0, 8),
    }));
  }, []);

  const updateHighlight = React.useCallback((index, field, value) => {
    setDraft((previous) => ({
      ...previous,
      highlights: (Array.isArray(previous.highlights) ? previous.highlights : []).map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      ),
    }));
  }, []);

  const removeHighlight = React.useCallback((index) => {
    setDraft((previous) => ({
      ...previous,
      highlights: (Array.isArray(previous.highlights) ? previous.highlights : []).filter((_, itemIndex) => itemIndex !== index),
    }));
  }, []);

  const dirty = React.useMemo(() => JSON.stringify(draft) !== JSON.stringify(form), [draft, form]);
  const section = SITE_SECTION_META[activeSection] || { label: "Sección", description: "Configurá esta parte de la landing." };
  const supportsAssets = ["identity", "hero", "spotlight", "brands", "about", "highlights"].includes(activeSection);

  const save = async ({ close = true } = {}) => {
    setError("");
    try {
      await onSave(draft);
      if (close) onClose();
      return true;
    } catch (err) {
      setError(err?.message || "No se pudieron guardar los cambios");
      return false;
    }
  };

  const openSettingsTab = async (tabIndex) => {
    if (dirty) {
      const saved = await save({ close: false });
      if (!saved) return;
    }
    onClose();
    onOpenSettings(tabIndex);
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4, overflow: "hidden", maxHeight: fullScreen ? "100%" : "92vh" } }}
    >
      <DialogTitle sx={{ px: { xs: 2, sm: 2.5 }, py: 2, borderBottom: "1px solid rgba(25,36,47,.09)" }}>
        <Stack direction="row" justifyContent="space-between" spacing={1.5} alignItems="flex-start">
          <Box>
            <Typography variant="overline" sx={{ color: BRAND.orange, fontWeight: 950, letterSpacing: 1 }}>
              EDITAR CONTENIDO
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark, lineHeight: 1.1 }}>
              {section.label}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.45, color: "rgba(25,36,47,.58)", maxWidth: 650 }}>
              {section.description}
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={saving} aria-label="Cerrar editor">
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 1.5, sm: 2.2 }, bgcolor: "rgba(238,240,245,.55)" }}>
        {error ? <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert> : null}
        {supportsAssets ? (
          <Alert
            severity="info"
            sx={{ mb: 1.5, borderRadius: 3, "& .MuiAlert-action": { alignItems: "center" } }}
            action={
              <Button size="small" startIcon={<ImageRoundedIcon />} onClick={() => openSettingsTab(1)} disabled={saving} sx={{ textTransform: "none", fontWeight: 900 }}>
                Recursos visuales
              </Button>
            }
          >
            Los textos se editan acá. Las imágenes se cargan desde un único lugar para evitar campos duplicados.
          </Alert>
        ) : null}

        <LandingInspector
          activeSection={activeSection}
          form={draft}
          productsEnabled={productsEnabled}
          setRoot={setRoot}
          setGroup={setGroup}
          setNested={setNested}
          addSlide={addSlide}
          updateSlide={updateSlide}
          removeSlide={removeSlide}
          addHighlight={addHighlight}
          updateHighlight={updateHighlight}
          removeHighlight={removeHighlight}
          simpleMode
          onOpenAssets={() => openSettingsTab(1)}
          onOpenContactSettings={() => openSettingsTab(3)}
        />
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 2.5 }, py: 1.6, borderTop: "1px solid rgba(25,36,47,.09)", bgcolor: "#fff" }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" sx={{ width: "100%" }}>
          <Typography variant="caption" sx={{ alignSelf: { sm: "center" }, color: dirty ? BRAND.orange : "rgba(25,36,47,.50)", fontWeight: 850 }}>
            {dirty ? "Tenés cambios sin guardar" : "Todos los cambios de esta sección están guardados"}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", fontWeight: 900 }}>
              Cerrar
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveRoundedIcon />}
              disabled={saving || !dirty}
              onClick={() => save({ close: true })}
              sx={{ borderRadius: 999, px: 2.4, bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, boxShadow: "none", "&:hover": { bgcolor: "#dc482c", boxShadow: "none" } }}
            >
              {saving ? "Guardando..." : "Guardar sección"}
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
